/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { EventGroup } from '@app/events/events-groups/event-group.model';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { WebsitesService } from '@app/settings/websites/websites.service';
import { Logger } from '@app/core';
import { EventGroupsService } from '@app/events/events-groups/event-groups.service';
import { ApiQuery } from '@app/core/http/api-query';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const log = new Logger('EventGroupsSeoFormComponent');

@UntilDestroy()
@Component({
  selector: 'app-event-group-seo-form',
  templateUrl: './event-group-seo-form.component.html',
  styleUrls: ['./event-group-seo-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventGroupsSeoFormComponent implements OnInit {
  @Input() eventGroupSeo: EventGroup = null;
  form: FormGroup;
  companyWebsiteLocales: Array<any>;
  currentWebsiteLocaleId: number;

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private websitesService: WebsitesService,
    private eventGroupService: EventGroupsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.createForm();
    this.getData();
  }

  save() {
    this.eventGroupService
      .updateTranslations(this.eventGroupSeo, { translations: this.form.get('websiteLocales').value })
      .pipe(untilDestroyed(this))
      .subscribe((res: any) => {
        this.modal.close(res);
      });
  }

  selectCompanyWebSiteLocale(id: number): void {
    this.currentWebsiteLocaleId = id;
  }

  getWebsiteLocaleName(id: number): string {
    const localeObj = this.companyWebsiteLocales.find((x) => x.id === id);
    return localeObj.website.name + ' ' + localeObj.website.url + ' ' + localeObj.locale.locale;
  }

  addFact(websiteLocale: any) {
    // @ts-ignore
    websiteLocale.controls.facts.push(this.getEmptyFact());
  }

  removeFact(websiteLocale: any, index: number) {
    log.debug('websiteLocale', websiteLocale);
    websiteLocale.get('facts')['controls'].splice(index, 1);
    websiteLocale.get('facts').updateValueAndValidity();
  }

  test() {
    log.debug('this.form', this.form);
  }

  addTab(websiteLocale: any) {
    // @ts-ignore
    websiteLocale.controls.tabs.push(this.getEmptyTab());
  }

  removeTab(websiteLocale: any, index: number) {
    log.debug('websiteLocale', websiteLocale);
    websiteLocale.get('tabs')['controls'].splice(index, 1);
    websiteLocale.get('tabs').updateValueAndValidity();
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      websiteLocales: new FormArray([]),
    });
  }

  private getData() {
    log.debug('getData');
    this.websitesService
      .getLocales()
      .pipe(untilDestroyed(this))
      .subscribe((websiteLocales: []) => {
        this.companyWebsiteLocales = websiteLocales;
        const formArray: FormArray = new FormArray([]);
        websiteLocales.forEach((websiteLocale: any) => {
          formArray.push(
            new FormGroup({
              company_website_locale_id: new FormControl(websiteLocale.id),
              description: new FormControl(null),
              extended_description: new FormControl(null),
              slug: new FormControl(null),
              meta_title: new FormControl(null),
              meta_description: new FormControl(null),
              video_url: new FormControl(null),
              facts: new FormArray([]),
              active: new FormControl(false),
              top: new FormControl(false),
              tabs: new FormArray([]),
              slogan: new FormControl(null),
            })
          );
        });
        this.form.setControl('websiteLocales', formArray);

        // Взема информацията от API-то и patch-ва контролите един по един
        const query = new ApiQuery().addIncludes('translations');
        this.eventGroupService
          .show(this.eventGroupSeo.id, query)
          .pipe(untilDestroyed(this))
          .subscribe((eventGroup: EventGroup) => {
            eventGroup.translations.forEach((translation: any) => {
              this.form.get('websiteLocales')['controls'].forEach((websiteLocaleControl: FormControl, ind: number) => {
                if (
                  translation.company_website_locale_id ===
                  Number(websiteLocaleControl.get('company_website_locale_id').value)
                ) {
                  if (translation.facts.length > 0) {
                    this.form
                      .get('websiteLocales')
                      ['controls'][ind].setControl('facts', this.createManyFacts(translation.facts));
                  }
                  if (translation.tabs.length > 0) {
                    this.form
                      .get('websiteLocales')
                      ['controls'][ind].setControl('tabs', this.createManyTabs(translation.tabs));
                  }
                  websiteLocaleControl.patchValue(translation);
                }
              });
              this.cdr.markForCheck();
            });
          });

        this.cdr.markForCheck();
      });
  }

  private getEmptyFact(): FormGroup {
    return new FormGroup({
      icon: new FormControl(null),
      count: new FormControl(null),
      label: new FormControl(null),
    });
  }

  private createManyFacts(facts: Array<any>): FormArray {
    const formArray: FormArray = new FormArray([]);
    facts.forEach(() => {
      formArray.push(this.getEmptyFact());
    });
    return formArray;
  }

  private getEmptyTab(): FormGroup {
    return new FormGroup({
      title: new FormControl(null),
      description: new FormControl(null),
    });
  }

  private createManyTabs(facts: Array<any>): FormArray {
    const formArray: FormArray = new FormArray([]);
    facts.forEach(() => {
      formArray.push(this.getEmptyTab());
    });
    return formArray;
  }
}
